/** Detaches whatever `init` attached. Returning one is optional but strongly preferred. */
export type Teardown = () => void;

export type ComponentInit = (element: HTMLElement) => void | Teardown | Promise<void | Teardown>;

export type RegistryEntry = {
	selector: string;
	load: () => Promise<ComponentInit>;
	select?: (root: ParentNode) => HTMLElement[];
	/** Matched against the document, not the caller's root: the CMS sets it on `<html>`, which
	    a re-scan rooted at an injected node can never see. */
	scope?: "document";
};

const INITIALISED = "data-fa-init";

/** Keyed by element, so a node removed from the DOM takes its teardowns with it. */
const teardowns = new WeakMap<HTMLElement, Teardown[]>();

/* Which entries have claimed each element. Keying the guard on the element alone lets the first
   entry to win the load race claim a node two selectors match, and the second silently never
   runs — SponsorBlock carries `data-fa-cascade` on its own root. */
const claimed = new WeakMap<HTMLElement, Set<string>>();

/* `root` is often the component itself — querySelector only looks down, so check it too. */
const matchesSelf = (root: ParentNode, selector: string): root is ParentNode & HTMLElement =>
	root instanceof HTMLElement && root.matches(selector);

const defaultSelect =
	(selector: string) =>
	(root: ParentNode): HTMLElement[] => {
		const descendants = Array.from(root.querySelectorAll<HTMLElement>(selector));
		return matchesSelf(root, selector) ? [root, ...descendants] : descendants;
	};

/** For a page-level behaviour: at most one element, however many carry the attribute. */
export const selectOne =
	(selector: string) =>
	(root: ParentNode): HTMLElement[] => {
		const element = matchesSelf(root, selector)
			? root
			: root.querySelector<HTMLElement>(selector);

		return element ? [element] : [];
	};

/** Initialises one element at a time so a single failure cannot take down the page. */
export const initEach = async (
	init: ComponentInit,
	elements: readonly HTMLElement[],
	label: string,
): Promise<void> => {
	for (const element of elements) {
		const labels = claimed.get(element) ?? new Set<string>();

		if (labels.has(label)) continue;

		// Set before awaiting, so a re-entrant call cannot double-attach.
		labels.add(label);
		claimed.set(element, labels);
		element.setAttribute(INITIALISED, "");

		try {
			const teardown = await init(element);

			if (typeof teardown === "function") {
				const existing = teardowns.get(element);
				if (existing) existing.push(teardown);
				else teardowns.set(element, [teardown]);
			}
		} catch (error) {
			labels.delete(label);
			if (labels.size === 0) element.removeAttribute(INITIALISED);
			console.warn(`[fa-ui] init failed for ${label}`, error);
		}
	}
};

/** Runs every entry whose selector matches inside root. Failures are contained per entry. */
export const runRegistry = async (
	entries: readonly RegistryEntry[],
	root: ParentNode = document,
): Promise<void> => {
	const scopeOf = (entry: RegistryEntry) => (entry.scope === "document" ? document : root);

	const matched = entries.filter((entry) => {
		const scope = scopeOf(entry);

		return matchesSelf(scope, entry.selector) || scope.querySelector(entry.selector) !== null;
	});

	await Promise.all(
		matched.map(async (entry) => {
			let init: ComponentInit;

			try {
				init = await entry.load();
			} catch (error) {
				console.warn(`[fa-ui] failed to load chunk for ${entry.selector}`, error);
				return;
			}

			try {
				const elements = (entry.select ?? defaultSelect(entry.selector))(scopeOf(entry));
				await initEach(init, elements, entry.selector);
			} catch (error) {
				console.warn(`[fa-ui] init failed for ${entry.selector}`, error);
			}
		}),
	);
};

/* The element alone, never its subtree: a document-scoped entry initialises on `<html>`, and
   passing that to `destroyDomScripts` tears down every component on the page. */
export const destroyElement = (element: HTMLElement): void => {
	if (!matchesSelf(element, `[${INITIALISED}]`)) return;

	for (const teardown of teardowns.get(element) ?? []) {
		try {
			teardown();
		} catch (error) {
			console.warn("[fa-ui] teardown failed", error);
		}
	}

	teardowns.delete(element);
	claimed.delete(element);
	element.removeAttribute(INITIALISED);
};

/** Reverses `runRegistry`. Only needed when the same elements are re-rendered in place. */
export const destroyDomScripts = (root: ParentNode = document): void => {
	const initialised = Array.from(root.querySelectorAll<HTMLElement>(`[${INITIALISED}]`));

	if (matchesSelf(root, `[${INITIALISED}]`)) initialised.push(root);

	for (const element of initialised) destroyElement(element);
};
